interface ReturnLocation {
  pathname: string;
  search: string;
  state?: unknown;
}

interface Router {
  location: { pathname?: string; search?: string; state?: unknown };
  params: { workspaceId?: string };
  push: (location: { pathname?: string; search?: string; state?: unknown }) => void;
  replace: (location: { pathname?: string; search?: string; state?: unknown }) => void;
}

function getReturnLocation(state: unknown, key: string): ReturnLocation | undefined {
  if (!state || typeof state !== 'object' || !(key in state)) return undefined;
  const destination = (state as Record<string, unknown>)[key];
  if (!destination || typeof destination !== 'object' || !('pathname' in destination) || !('search' in destination)) return undefined;
  if (typeof destination.pathname !== 'string' || typeof destination.search !== 'string') return undefined;
  if (!/^\/workspace\/\d+\/(customers|orders|routes)$/.test(destination.pathname)) return undefined;
  return {
    pathname: destination.pathname,
    search: destination.search,
    ...('state' in destination ? { state: destination.state } : {}),
  };
}

export const getCustomerOrderReturn = (state: unknown) => getReturnLocation(state, 'customerOrderHistoryReturn');
export const getCustomerProfileReturn = (state: unknown) => getReturnLocation(state, 'customerProfileReturn');

function currentLocation(router: Router): ReturnLocation {
  return {
    pathname: router.location.pathname ?? '',
    search: router.location.search ?? '',
    ...(router.location.state ? { state: router.location.state } : {}),
  };
}

export function openLinkedCustomer(router: Router, customerId: string) {
  const origin = currentLocation(router);
  const onOrdersPage = origin.pathname.endsWith('/orders');
  const onCustomersPage = origin.pathname.endsWith('/customers');
  const params = new URLSearchParams(onOrdersPage || onCustomersPage ? origin.search : '');
  params.set('customer', customerId);
  if (!onOrdersPage) params.delete('order');
  router.push({
    pathname: onOrdersPage || onCustomersPage ? origin.pathname : `/workspace/${router.params.workspaceId}/customers`,
    search: params.toString(),
    state: { customerProfileReturn: origin },
  });
}

export function closeCustomerOrderPanels(router: Router) {
  const params = new URLSearchParams(router.location.search);
  params.delete('customer');
  params.delete('order');
  router.replace({ search: params.toString(), state: undefined });
}

export function openCustomerOrder(
  router: Router,
  customerId: string,
  order: { uuid?: string | null; projectId?: string | number | null },
) {
  if (!order.uuid || !order.projectId) return;
  const pathname = router.location.pathname ?? '';
  const customerOrderHistoryReturn = currentLocation(router);
  if (router.params.workspaceId === String(order.projectId)) {
    const params = new URLSearchParams(router.location.search);
    if (pathname.endsWith('/customers')) params.set('customer', customerId);
    else params.delete('customer');
    params.set('order', order.uuid);
    router.push({ search: params.toString(), state: { customerOrderHistoryReturn } });
  } else {
    router.push({
      pathname: `/workspace/${order.projectId}/orders`,
      search: new URLSearchParams({ order: order.uuid }).toString(),
      state: { customerOrderHistoryReturn },
    });
  }
}
